import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  Heading,
  ModalHeader,
  Grid,
  GridItem,
  Flex,
  IconButton,
  Text,
  Image,
  Center,
  useToast,
} from "@chakra-ui/react";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import {
  AI,
  CSV,
  DOCX,
  EPS,
  JPG,
  PDF,
  PNG,
  PPT,
  PSD,
  XSL,
  XSL as XLSX,
} from "../../assets/new-images/fileIcons";
import { InputText } from "../Input";
import baseService from "../../services/base";
import { decodificarNomeDeUpload, nomeDoArquivo } from "../../utils/nome-de-upload";
import { AttentionModal } from "./Attention";

const fileIconMap = { AI, CSV, DOCX, EPS, JPG, PDF, PNG, PPT, PSD, XSL, XLSX };

const MAX_FILENAME_LENGTH = 74;
const truncateFileName = (name = "") =>
  name.length > MAX_FILENAME_LENGTH
    ? `${name.slice(0, MAX_FILENAME_LENGTH)}...`
    : name;

// EG-73: o nome chega do banco possivelmente gravado como latin-1. Decodificar
// aqui, e nao em cada call site, garante que todo uso deste componente ja pegue
// o nome legivel. A funcao e idempotente: upload novo passa incolume.
function FileName({ filename: filenameCru = "" }) {
  const filename = decodificarNomeDeUpload(filenameCru);
  const fileExtension = filename.split(".").pop()?.toUpperCase() || "";
  const icon = fileIconMap[fileExtension];
  return (
    <Flex h="32px" align="center" gap="12px">
      {icon && <Image src={icon} alt={`${fileExtension} icon`} />}
      <Text
        as="span"
        fontSize="sm"
        lineHeight="19.6px"
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
      >
        {truncateFileName(filename)}
      </Text>
    </Flex>
  );
}

export default function AttachEvidenceModal({ isOpen, onClose, files: propFiles }) {
  const toast = useToast();
  const [files, setFiles] = useState([]);
  // Exclusão via AttentionModal padrão com DUPLA confirmação (step 1 → step 2).
  const [del, setDel] = useState(null); // { file, step }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFiles(propFiles || []);
    setDel(null);
  }, [isOpen, propFiles]);

  function confirmDelete() {
    if (!del) return;
    if (del.step === 1) {
      setDel({ file: del.file, step: 2 });
      return;
    }
    handleDelete(del.file);
  }

  async function handleDownload(file) {
    try {
      const response = await baseService.get(
        `/question-response/download?key=${file?.file_path}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      // EG-73: o arquivo tem de cair no disco com o nome legivel tambem — nome bonito
      // so na tela seria meio conserto. `file_path` acima segue CRU: e a chave do S3.
      link.setAttribute("download", nomeDoArquivo(file, "evidencia"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Erro ao baixar evidência", status: "error" });
    }
  }

  async function handleDelete(file) {
    const idResponse = file?.id_question_response;
    const idFile = file?.id_question_response_files;
    if (!idResponse || !idFile) return;
    setDeleting(true);
    try {
      // Exclusão de 1 arquivo reusa o endpoint de upload com flag delete (mesmo
      // contrato do form em useFileUpload.ts): sem 'file', backend cai no ramo delete.
      const formData = new FormData();
      formData.append(
        "jsonPayload",
        JSON.stringify({ id_question_response_file: idFile, delete: true })
      );
      await baseService.post(
        `/question-response/upload/${idResponse}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setFiles((prev) =>
        prev.filter((f) => f.id_question_response_files !== idFile)
      );
      toast({ title: "Evidência excluída", status: "success" });
    } catch (error) {
      toast({ title: "Erro ao excluir evidência", status: "error" });
    } finally {
      setDeleting(false);
      setDel(null);
    }
  }

  const delName =
    nomeDoArquivo(del?.file, "esta evidência");

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent
        maxW="1329px"
        minH="361px"
        p="32px 60px"
        borderRadius="18px"
        overflow="hidden"
      >
        <ModalHeader minH="32px" p="0">
          <Heading fontSize={["18px", "20px"]} lineHeight="normal">
            Arquivos anexados
          </Heading>
        </ModalHeader>

        <ModalBody p="32px 0">
          <Grid
            templateColumns="40% 40% 20%"
            gap="16px"
            p="11px 12px"
            color="#16262E"
            fontSize="13px"
            fontWeight={500}
            bg="#DADADA"
            h="41px"
          >
            <GridItem whiteSpace="nowrap">Descrição</GridItem>
            <GridItem whiteSpace="nowrap">Arquivo</GridItem>
            <GridItem whiteSpace="nowrap">Ações</GridItem>
          </Grid>

          {files?.length === 0 && (
            <Center h="64px" fontSize="sm" color="#818A8F">
              Nenhuma evidência anexada
            </Center>
          )}

          {files?.map((file, index) => {
            const idFile = file?.id_question_response_files;
            const canDelete = !!file?.id_question_response && !!idFile;
            return (
              <Grid
                key={idFile ?? index}
                templateColumns="40% 40% 20%"
                gap="16px"
                bg="#FEFEFE"
                height="64px"
                pl="12px"
                color="#16262E"
              >
                <GridItem alignContent="center">
                  <InputText
                    defaultValue={file?.description || ""}
                    readOnly
                    isDisabled
                    h="48px"
                  />
                </GridItem>
                <GridItem alignContent="center">
                  <FileName filename={nomeDoArquivo(file)} />
                </GridItem>
                <GridItem alignContent="center">
                  <Flex align="center" gap="8px">
                    <IconButton
                      aria-label="Baixar evidência"
                      icon={<FiDownload />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleDownload(file)}
                      isDisabled={!file?.file_path}
                    />
                    <IconButton
                      aria-label="Excluir evidência"
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => setDel({ file, step: 1 })}
                      isDisabled={!canDelete}
                    />
                  </Flex>
                </GridItem>
              </Grid>
            );
          })}
        </ModalBody>

        <ModalFooter p={0}>
          <Button variant="secondary" w="full" height="50px" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <AttentionModal
      isOpen={!!del}
      onClose={() => !deleting && setDel(null)}
      submit={confirmDelete}
      isLoading={deleting}
      redType
      title={
        del?.step === 2
          ? "Confirmar exclusão da evidência"
          : "Excluir esta evidência?"
      }
      content={
        del?.step === 2
          ? `Confirma excluir definitivamente "${delName}"? Esta ação não pode ser desfeita. Esta é a segunda confirmação.`
          : "O arquivo será removido permanentemente (S3 + banco). Deseja continuar?"
      }
    />
    </>
  );
}
